import type { FC, ReactNode } from 'react';

export type PlatformNavExtra = { label: string; view: string };

export type PlatformNavStripProps = {
  onNavigate: (view: string) => void;
  backView: string;
  backLabel?: string;
  title?: string;
  extras?: PlatformNavExtra[];
  children?: ReactNode;
  /** Anchor id for skip-link target (default platform-main) */
  mainId?: string;
};

/**
 * Sticky top bar: consistent back navigation + optional shortcuts for platform / marketing pages.
 * Use with a sibling `<main id={mainId}>` for skip-link accessibility.
 */
export const PlatformNavStrip: FC<PlatformNavStripProps> = ({
  onNavigate,
  backView,
  backLabel = 'Back',
  title,
  extras = [],
  children,
  mainId = 'platform-main',
}) => (
  <>
    <a
      href={`#${mainId}`}
      className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[100] focus:rounded-lg focus:bg-slate-900 focus:px-4 focus:py-2 focus:text-sm focus:text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
    >
      Skip to main content
    </a>
    <header
      className="sticky top-0 z-40 border-b border-white/10 bg-slate-950/95 backdrop-blur-md"
      role="banner"
      aria-label="Platform navigation"
    >
      <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-2 px-4 py-3">
        <button
          type="button"
          onClick={() => onNavigate(backView)}
          className="rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm font-medium text-white/90 transition-colors hover:bg-white/10"
        >
          ← {backLabel}
        </button>
        {title ? (
          <span className="text-sm font-semibold text-white/80 md:border-l md:border-white/10 md:pl-4">
            {title}
          </span>
        ) : null}
        <div className="flex flex-1 flex-wrap items-center justify-end gap-2">
          {extras.map((e) => (
            <button
              key={e.view}
              type="button"
              onClick={() => onNavigate(e.view)}
              className="rounded-lg border border-white/10 px-3 py-2 text-xs font-medium text-white/75 transition-colors hover:bg-white/10"
            >
              {e.label}
            </button>
          ))}
          {children}
        </div>
      </div>
    </header>
  </>
);
